import axios from "axios";
import { newApiBaseUrl } from "../../axiosInstance";

const supplierPriceHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `bearer ${localStorage.jwtToken}`,
  "X-Tenant": "hamazon",
});

const attachSupplierPriceApiError = (error) => {
  const data = error?.response?.data;
  const apiErrorMessage =
    (typeof data?.error === "string" && data.error) ||
    (typeof data?.message === "string" && data.message) ||
    (typeof data === "string" && data) ||
    null;
  if (apiErrorMessage && error && typeof error === "object") {
    error.apiErrorMessage = apiErrorMessage;
  }
  return error;
};

export const formatSupplierPriceDate = (date = new Date()) => {
  const d = date instanceof Date ? date : new Date(date);
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()}`;
};

/** API promo dates use `YYYY-MM-DD-HH`; date inputs use `YYYY-MM-DD`. */
export const supplierPromoDateToInput = (raw) => {
  if (!raw) return "";
  const trimmed = String(raw).trim();
  const ymdh = /^(\d{4})-(\d{2})-(\d{2})-(\d{2})$/.exec(trimmed);
  if (ymdh) return `${ymdh[1]}-${ymdh[2]}-${ymdh[3]}`;
  const ymd = /^(\d{4})-(\d{2})-(\d{2})/.exec(trimmed);
  if (ymd) return `${ymd[1]}-${ymd[2]}-${ymd[3]}`;
  const dmy = /^(\d{2})-(\d{2})-(\d{4})$/.exec(trimmed);
  if (dmy) return `${dmy[3]}-${dmy[2]}-${dmy[1]}`;
  return "";
};

export const supplierPromoDateFromInput = (raw) => {
  if (!raw) return undefined;
  const trimmed = String(raw).trim();
  if (/^\d{4}-\d{2}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return `${trimmed}-00`;
  return trimmed;
};

export const GetSupplierPrices = async ({
  gtin,
  supplierId,
  date,
  history = false,
  includePromotions = false,
} = {}) => {
  if (!gtin && !supplierId) {
    return null;
  }

  const params = {};
  if (gtin) params.gtin = gtin;
  if (supplierId) params.supplierId = supplierId;
  if (date) params.date = date;
  if (history) params.history = "true";
  if (includePromotions && !history) params.includePromotions = "true";

  try {
    const response = await axios.get(`${newApiBaseUrl}/supplier-prices`, {
      params,
      headers: supplierPriceHeaders(),
    });
    return response.data ?? null;
  } catch (error) {
    console.error("Error fetching supplier prices:", error);
    throw error;
  }
};

export const UpdateSupplierPrice = async ({
  supplierId,
  gtin,
  date,
  price,
  currency = "ILS",
  discountPercent,
}) => {
  const body = { price, currency };
  if (discountPercent != null && Number.isFinite(Number(discountPercent))) {
    body.discountPercent = Number(discountPercent);
  }

  const response = await axios.put(
    `${newApiBaseUrl}/prices/suppliers/${supplierId}/products/${gtin}/dates/${date}`,
    body,
    { headers: supplierPriceHeaders() },
  );
  return response.data;
};

export const createSupplierPromotion = async (payload) => {
  try {
    const response = await axios.post(
      `${newApiBaseUrl}/supplier-promotions`,
      payload,
      { headers: supplierPriceHeaders() },
    );
    return response.data;
  } catch (error) {
    console.error("Error creating supplier promotion:", error);
    throw attachSupplierPriceApiError(error);
  }
};

export const updateSupplierPromotion = async (promoId, payload) => {
  try {
    const response = await axios.patch(
      `${newApiBaseUrl}/supplier-promotions/${promoId}`,
      payload,
      { headers: supplierPriceHeaders() },
    );
    return response.data;
  } catch (error) {
    console.error("Error updating supplier promotion:", error);
    throw attachSupplierPriceApiError(error);
  }
};

export const deleteSupplierPromotion = async (promoId) => {
  try {
    const response = await axios.delete(
      `${newApiBaseUrl}/supplier-promotions/${promoId}`,
      { headers: supplierPriceHeaders() },
    );
    return response.data;
  } catch (error) {
    console.error("Error deleting supplier promotion:", error);
    throw attachSupplierPriceApiError(error);
  }
};
